<template>
    <!-- 사내 디자인 시스템 PageHeader 와 같은 모양·수치: 좌우 16 · 위아래 24, 제목 22/650 · 설명 13, 제목 → 설명 8, 버튼 사이 8 -->
    <div class="ds-page-header" :class="{ 'is-contained': header.contained, 'has-tabs': !!header.tabs }">
        <div :class="header.contained ? 'container' : 'ds-ph-inner'">
        <div class="ds-ph-row">
            <!-- 나가는 길: 주소(to)면 링크, 동작(onClick — 예: 목록에서 왔으면 브라우저 뒤로)이면 버튼 -->
            <router-link v-if="header.back && header.back.to" :to="header.back.to" class="ds-ph-back"
                         :aria-label="header.back.label" :title="header.back.label">
                <AppIcon name="chevron-left"/>
            </router-link>
            <button v-else-if="header.back && header.back.onClick" type="button" class="ds-ph-back"
                    :disabled="!!header.back.disabled"
                    :aria-label="header.back.label" :title="header.back.label" @click="header.back.onClick">
                <AppIcon name="chevron-left"/>
            </button>
            <div class="ds-ph-text">
                <div class="ds-ph-title-row">
                    <h1 class="ds-ph-title">{{ header.title }}</h1>
                    <template v-if="header.badge"><span class="ds-ph-badge"><component :is="slotHost" :render-fn="header.badge"/></span></template>
                </div>
                <p v-if="header.description" class="ds-ph-desc">{{ header.description }}</p>
            </div>
            <div v-if="header.actions" class="ds-ph-actions">
                <component :is="slotHost" :render-fn="header.actions"/>
            </div>
        </div>
        <!-- 탭 — 제목 판 아래 선에 붙는다(활성 = 아래 선, 상단 바 메뉴와 같은 말투) -->
        <nav v-if="header.tabs" class="ds-ph-tabs" aria-label="화면 안 메뉴">
            <component :is="slotHost" :render-fn="header.tabs"/>
        </nav>
        </div>
    </div>
</template>

<script>
// 화면이 넘긴 슬롯 함수를 여기서 그린다 — 함수 안에서 읽는 화면 데이터가 바뀌면 이 자리도 다시 그려진다
const SlotHost = {
    functional: true,
    props: {renderFn: {type: Function, required: true}},
    render(h, {props}) {
        return props.renderFn({});
    },
};

export default {
    name: 'AdminPageHeaderView',
    props: {
        header: {type: Object, required: true},
    },
    data() {
        return {slotHost: SlotHost};
    },
};
</script>

<style lang="scss">
.ds-page-header {
    padding: 24px 16px;
    border-bottom: 1px solid var(--ds-border);
    background-color: var(--ds-surface);
}

// 판은 화면 끝까지, 내용은 본문과 같은 폭 · 같은 가운데 — 본문(관리자 셸 main · 원청 .ds-top-body)이 최대 1680 · 좌우 16 이라
// 내용 폭 1648 을 가운데에 둔다. 화면이 1680 보다 넓어져도 제목과 본문의 세로선이 맞는다
.ds-ph-inner {
    max-width: 1648px;
    margin: 0 auto;
}

// 본문이 가운데 고정폭인 화면 — 판은 화면 끝까지, 내용은 bootstrap container 안(좌우 여백은 container 가 갖는다)
.ds-page-header.is-contained {
    padding-right: 0;
    padding-left: 0;
}

.ds-page-header.has-tabs {
    padding-bottom: 0;
}

.ds-ph-tabs {
    display: flex;
    gap: 4px;
    margin-top: 16px;
    margin-left: -12px;
    overflow-x: auto;
}

.ds-ph-tab {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: 40px;
    padding: 0 12px;
    font-size: 14px;
    white-space: nowrap;
    color: var(--ds-fg-secondary);

    &:hover {
        color: var(--ds-fg);
        text-decoration: none;
    }

    &.is-active {
        font-weight: 500;
        color: var(--ds-accent-text);

        &::after {
            position: absolute;
            right: 8px;
            bottom: 0;
            left: 8px;
            height: 2px;
            border-radius: var(--ds-radius-badge);
            background-color: var(--ds-accent);
            content: "";
        }
    }

    &:focus-visible {
        outline: 2px solid var(--ds-ring);
        outline-offset: -2px;
    }

    // 화면이 불러오는 중 — 전에는 로딩 막이 탭까지 덮었다
    &.is-disabled {
        opacity: .5;
        pointer-events: none;
    }
}

.ds-ph-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px 16px;
}

.ds-ph-back {
    padding: 0;
    border: 0;
    background: transparent;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 28px;
    height: 30px;
    margin-left: -4px;
    border-radius: var(--ds-radius-control);
    color: var(--ds-fg-muted);
    font-size: 20px;

    &:hover {
        background-color: var(--ds-surface-sunken);
        color: var(--ds-fg);
    }

    &:disabled {
        opacity: .4;
        pointer-events: none;
    }
}

.ds-ph-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.ds-ph-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.ds-ph-title {
    margin: 0;
    font-size: 22px;
    font-weight: 650;
    line-height: 30px;
    letter-spacing: -0.01em;
    color: var(--ds-fg);
}

.ds-ph-desc {
    max-width: 65ch;
    margin: 0;
    font-size: 13px;
    line-height: 20px;
    color: var(--ds-fg-muted);
}

.ds-ph-actions {
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
</style>
